import {Component, ElementRef, Injector, Input, OnInit, ViewChild} from '@angular/core';
import {ModalPageController} from '../../Core/page-controller/modal-page-controller';
import {VideoViewer} from '../../../Models/Shared/video-viewer.interface';

@Component({
  selector: 'app-videos-viewer',
  templateUrl: './videos-viewer.page.html',
  styleUrls: ['./videos-viewer.page.scss'],
  standalone: false
})
export class VideosViewerPage extends ModalPageController implements OnInit {

  @ViewChild('video') video!: ElementRef<HTMLVideoElement>;

  @Input() urls: string[] = [];

  videos: VideoViewer[] = [];

  activeIndex: number = 0;
  progress: number = 0;
  animationFrameId: number | null = null;
  seekTimeout: any = null;

  isPaused: boolean = false;
  isDragging: boolean = false;
  isVideoLoading: boolean = true;

  constructor(
    protected override injector: Injector,
  ) {
    super(injector);
  }

  ngOnInit() {
    this.videos = this.urls.map((url) => ({
      url: url,
      duration: 0
    }));
  }


  /**
   * Obtener la duración de cada video
   *
   */
  onVideoLoaded(video: HTMLVideoElement, index: number) {
    this.videos[index].duration = video.duration;
  }


  /**
   * Cuando el video puede reproducirse
   *
   */
  onVideoCanPlay(index: number) {

    if (index !== this.activeIndex) {
      return;
    }

    this.isVideoLoading = false;
    this.playVideo();
  }


  /**
   * Reproducir video
   *
   */
  playVideo() {
    const playPromise = this.video.nativeElement.play();

    if (playPromise !== undefined) {
      playPromise.catch(() => this.onVideoError());
    }

    this.isPaused = false;
    this.setProgress();
  }


  /**
   * Pausar video
   *
   */
  pauseVideo() {
    this.video.nativeElement.pause();
    this.isPaused = true;

    this.cancelProgress();
  }


  /**
   * Toggle play/pause
   *
   */
  togglePlayPause() {

    if (this.isPaused) {
      this.playVideo();
      return;
    }

    this.pauseVideo();
  }


  /**
   * Siguiente video
   *
   */
  nextVideo() {

    if (this.isDragging || this.videos.length <= 1) {
      return;
    }

    this.cancelProgress();
    this.isVideoLoading = true;

    if (this.activeIndex >= this.videos.length - 1) {
      this.exitViewer();
      return;
    }

    this.activeIndex++;
    this.resetVideoTime();
  }


  /**
   * Anterior video
   *
   */
  previousVideo() {

    if (this.videos.length <= 1) {
      return;
    }

    this.cancelProgress();
    this.isVideoLoading = true;

    if (this.activeIndex === 0) {
      this.video.nativeElement.currentTime = 0;
      this.isVideoLoading = false;

      this.playVideo();
      return;
    }

    this.activeIndex--;
    this.resetVideoTime();
  }


  /**
   * Establecer el progreso del video
   *
   */
  setProgress() {

    this.cancelProgress();

    const duration: number = this.video.nativeElement.duration;

    const update = () => {

      if (!this.video.nativeElement.paused && !this.video.nativeElement.ended) {
        const currentTime: number = this.video.nativeElement.currentTime;
        this.progress = (currentTime / duration) * 100;

        if (this.progress >= 100) {
          this.progress = 100;
          this.nextVideo();
          return;
        }

        this.animationFrameId = requestAnimationFrame(update);
      }

    };

    this.animationFrameId = requestAnimationFrame(update);
  }


  /**
   * Cancelar progreso
   *
   */
  cancelProgress() {

    if (this.animationFrameId === null) {
      return;
    }

    cancelAnimationFrame(this.animationFrameId);
    this.animationFrameId = null;
  }


  /**
   * Cambiar posición del video usando el slider
   *
   */
  onProgressChange(event: any) {
    const newProgress: number = event.detail.value;
    this.progress = newProgress;

    if (this.seekTimeout) {
      clearTimeout(this.seekTimeout);
    }

    this.seekTimeout = setTimeout(() => {
      const duration: number = this.video.nativeElement.duration;
      this.video.nativeElement.currentTime = (newProgress / 100) * duration;
    }, 300);
  }


  /**
   * Iniciar arrastre del slider
   *
   */
  onProgressStart() {
    this.isDragging = true;
    this.cancelProgress();

    if (this.seekTimeout) {
      clearTimeout(this.seekTimeout);
    }
  }


  /**
   * Finalizar arrastre del slider
   *
   */
  onProgressEnd() {
    this.isDragging = false;

    const duration: number = this.video.nativeElement.duration;
    this.video.nativeElement.currentTime = (this.progress / 100) * duration;

    if (!this.isPaused) {
      this.playVideo();
    }
  }


  /**
   * Reiniciar tiempo del video
   *
   */
  resetVideoTime() {
    this.isPaused = false;

    this.progress = 0;
    this.video.nativeElement.currentTime = 0;
  }


  /**
   * Limpiar todos los timers y animaciones
   *
   */
  cleanupAll() {
    this.cancelProgress();

    if (this.seekTimeout) {
      clearTimeout(this.seekTimeout);
      this.seekTimeout = null;
    }
  }


  /**
   * Manejar error de reproducción del video
   *
   */
  onVideoError() {
    this.cancelProgress();
    this.isVideoLoading = false;

    if (this.activeIndex < this.videos.length - 1) {
      this.activeIndex++;
      this.resetVideoTime();
      return;
    }

    this.exitViewer();
  }


  /**
   * Salir del visor
   *
   */
  exitViewer() {
    this.cleanupAll();
    this.dismiss();
  }

}
